import React from 'react';
import { cn } from '@/lib/utils';

interface LogoProps {
  variant?: 'horizontal' | 'stacked' | 'icon-only';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  type?: 'color' | 'white';
  theme?: 'light' | 'dark';
}

const Logo: React.FC<LogoProps> = ({
  variant = 'horizontal',
  size = 'md',
  className,
  type = 'color',
  theme = 'light'
}) => {
  // Size mappings based on Eduvos guidelines (45mm minimum threshold)
  // Note: sizeClasses used in child components

  // Determine logo variant based on theme/type
  const logoType = theme === 'dark' || type === 'white' ? 'white' : 'color';
  
  // Size mappings based on Eduvos guidelines (45mm minimum threshold)
  const sizeClasses = {
    sm: 'h-8',      // Below 45mm - no slogan per guidelines
    md: 'h-12',     // Standard size
    lg: 'h-16',     // Large size
    xl: 'h-20'      // Extra large size
  };

  const baseClasses = cn(
    'flex items-center',
    className
  );

  // For icon-only variant, we'll extract just the icon from the logo
  // This would require separate icon assets or SVG extraction
  if (variant === 'icon-only') {
    return (
      <div className={baseClasses}>
        <EduvosIcon size={size} type={logoType} />
      </div>
    );
  }

  // Get the appropriate logo image path
  const getLogoPath = () => {
    const baseUrl = '/assets/logos/eduvos';
    const variantPath = variant === 'stacked' ? 'stacked' : 'horizontal';
    return `${baseUrl}-${variantPath}-${logoType}.png`;
  };

  return (
    <div className={baseClasses}>
      <img
        src={getLogoPath()}
        alt="Eduvos"
        className={cn(
          sizeClasses[size as keyof typeof sizeClasses],
          'object-contain'
        )}
        style={{
          // Ensure proper aspect ratio is maintained
          maxWidth: 'none'
        }}
      />
    </div>
  );
};

// Eduvos Icon Component (Crown/Shield) - for icon-only variant
const EduvosIcon: React.FC<{ size: string; type: 'color' | 'white' }> = ({ size, type }) => {
  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-12 w-12',
    lg: 'h-16 w-16',
    xl: 'h-20 w-20'
  };

  // Use the official Eduvos shield logo
  const logoPath = type === 'color' 
    ? '/assets/logos/Eduvos Stacked Logo_Only-Colour.png'
    : '/assets/logos/Eduvos Stacked Logo_only_White.png';

  return (
    <img
      src={logoPath}
      alt="Eduvos"
      className={cn(sizeClasses[size as keyof typeof sizeClasses], 'flex-shrink-0 object-contain')}
    />
  );
};


// Department/Faculty Lockup Component
interface DepartmentLogoProps extends LogoProps {
  department: string;
  faculty?: 'law' | 'humanities' | 'applied-science' | 'it';
}

export const DepartmentLogo: React.FC<DepartmentLogoProps> = ({
  department,
  faculty,
  ...logoProps
}) => {
  const facultyColors = {
    law: 'text-faculty-law',
    humanities: 'text-faculty-humanities',
    'applied-science': 'text-faculty-applied-science',
    it: 'text-faculty-it'
  };

  return (
    <div className="flex items-center space-x-4">
      <Logo {...logoProps} />
      <div className="h-8 w-px bg-heritage-blue/30" /> {/* Hairline separator */}
      <div className="flex flex-col">
        <div className={cn(
          'font-secondary font-medium text-sm',
          faculty ? facultyColors[faculty] : 'text-heritage-blue'
        )}>
          {department}
        </div>
      </div>
    </div>
  );
};

// Co-branding Component
interface CoBrandingProps {
  partnerLogo: React.ReactNode;
  partnerName: string;
  layout?: 'horizontal' | 'stacked';
  eduvosSize?: 'sm' | 'md' | 'lg' | 'xl';
}

export const CoBrandingLogo: React.FC<CoBrandingProps> = ({
  partnerLogo,
  partnerName,
  layout = 'horizontal',
  eduvosSize = 'md'
}) => {
  if (layout === 'stacked') {
    return (
      <div className="flex flex-col items-center space-y-4">
        <Logo size={eduvosSize} variant="horizontal" />
        <div className="w-full h-px bg-heritage-blue/30" />
        <div className="flex items-center space-x-2">
          {partnerLogo}
          <span className="font-secondary text-sm text-muted-foreground">
            {partnerName}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-6">
      <Logo size={eduvosSize} variant="horizontal" />
      <div className="h-8 w-px bg-heritage-blue/30" />
      <div className="flex items-center space-x-2">
        {partnerLogo}
        <span className="font-secondary text-sm text-muted-foreground">
          {partnerName}
        </span>
      </div>
    </div>
  );
};

export default Logo;
